import React from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useReads } from "../hooks/useReads";
import { getScnToken } from "../web3/contracts";
import { fmtUsdt, fmtScn, shortAddress, parseUsdt } from "../utils/format";
import { SCN_DECIMALS, ACTIVE_CONTRACTS } from "../config/app.config";
import StatGrid from "../components/admin/StatGrid";
import ActionForm from "../components/admin/ActionForm";
import PauseControl from "../components/admin/PauseControl";
import RoleBanner from "../components/admin/RoleBanner";
import AddressBar from "../components/admin/AddressBar";

const addr = (k, label, ph = "0x...") => ({ key: k, label, placeholder: ph });
const parseScn = (v) => ethers.utils.parseUnits(String(v || "0"), SCN_DECIMALS);

export default function ScnTokenAdmin() {
  const { signer } = useWeb3();
  const { data, reload } = useReads(async (provider) => {
    const t = getScnToken(provider);
    const [owner, admin, smartcore, scnDex, paused, liq, supply, initPrice, price, physical] = await Promise.all([
      t.owner(), t.admin(), t.smartcore(), t.scnDex(), t.paused(),
      t.liquidityUSDT(), t.totalSupply(), t.initialPrice(), t.getPrice(), t.usdtPhysicalBalance(),
    ]);
    return { owner, admin, smartcore, scnDex, paused, liq, supply, initPrice, price, physical };
  });

  const C = (s) => getScnToken(s);

  return (
    <div className="stack">
      <AddressBar label="SCN Token Contract" address={ACTIVE_CONTRACTS.scnToken} />
      <RoleBanner owner={data?.owner} admin={data?.admin} />

      <StatGrid
        title="SCN Token — Overview" icon="coins" accent="violet" onRefresh={reload}
        rows={data ? [
          { label: "Owner", value: shortAddress(data.owner), mono: true },
          { label: "Admin", value: shortAddress(data.admin), mono: true },
          { label: "Smartcore", value: shortAddress(data.smartcore), mono: true },
          { label: "SCN DEX", value: shortAddress(data.scnDex), mono: true },
          { label: "Paused", value: data.paused ? "Yes" : "No" },
          { label: "Price", value: `$${fmtUsdt(data.price, 4)}` },
          { label: "Initial Price", value: `$${fmtUsdt(data.initPrice, 4)}` },
          { label: "Liquidity (book)", value: `${fmtUsdt(data.liq)} USDT` },
          { label: "Physical USDT", value: `${fmtUsdt(data.physical)} USDT` },
          { label: "Total Supply", value: `${fmtScn(data.supply)} SCN` },
        ] : []}
      />

      <PauseControl paused={data?.paused} getContract={C} signer={signer} onDone={reload} />

      <div className="adm-section-title">Pricing</div>
      <div className="adm-grid">
        <ActionForm title="Set Initial Price" desc="Pre-bootstrap price (supply = 0)." icon="gauge" accent="cyan"
          fields={[{ key: "p", label: "Price (USDT)", placeholder: "0.10", numeric: true }]}
          button="Set Initial Price" onDone={reload}
          onRun={(v) => C(signer).setInitialPrice(parseUsdt(v.p))} />

        <ActionForm title="Set Price Manually" desc="Reconcile liquidity to target price (needs USDT backing)." icon="gauge" accent="gold"
          fields={[{ key: "p", label: "Price (USDT)", placeholder: "0.25", numeric: true }]}
          button="Set Price" onDone={reload}
          onRun={(v) => C(signer).setPriceManually(parseUsdt(v.p))} />
      </div>

      <div className="adm-section-title">Mint & withdraw</div>
      <div className="adm-grid">
        <ActionForm title="Mint SCN" desc="Discretionary mint (dilutes price)." icon="coins" accent="violet"
          fields={[addr("a", "To address"), { key: "amt", label: "Amount (SCN)", placeholder: "100", numeric: true }]}
          button="Mint SCN" onDone={reload}
          onRun={(v) => C(signer)._Mint(v.a, parseScn(v.amt))} />

        <ActionForm title="Withdraw Liquidity (owner)" desc="Pulls USDT from backing (emergency)." icon="shield" danger
          fields={[addr("a", "To address"), { key: "amt", label: "Amount (USDT)", placeholder: "100", numeric: true }]}
          button="Withdraw USDT" onDone={reload}
          onRun={(v) => C(signer)._claimSCN(v.a, parseUsdt(v.amt))} />
      </div>

      <div className="adm-section-title">Wiring & roles</div>
      <div className="adm-grid">
        <ActionForm title="Set Smartcore" icon="node" fields={[addr("a", "SmartcoreNodePurchase address")]} button="Set Smartcore" onDone={reload} onRun={(v) => C(signer).setSmartcore(v.a)} />
        <ActionForm title="Set SCN DEX" icon="spark" fields={[addr("a", "SCN DEX address")]} button="Set SCN DEX" onDone={reload} onRun={(v) => C(signer).setScnDex(v.a)} />
        <ActionForm title="Set Admin (owner)" icon="shield" fields={[addr("a", "New admin address")]} button="Set Admin" onDone={reload} onRun={(v) => C(signer).setAdmin(v.a)} />
        <ActionForm title="Transfer Ownership" desc="Irreversible." icon="shield" danger fields={[addr("a", "New owner address")]} button="Transfer Ownership" onDone={reload} onRun={(v) => C(signer).transferOwnership(v.a)} />
      </div>
    </div>
  );
}
